import React, { useState, useEffect } from 'react';
import { Question } from '../../types';
import { Box, TextField, Typography, CircularProgress } from '@mui/material';
import { fetchQuestionSummary } from '../../services/api';
import { SubmitButton } from './SubmitButton';

interface CallbackSummaryQuestionProps {
  question: Question;
  onSubmit: (response: any) => Promise<void>;
  isSubmitting?: boolean;
}

export const CallbackSummaryQuestion: React.FC<CallbackSummaryQuestionProps> = ({
  question,
  onSubmit,
  isSubmitting = false,
}) => {
  const [summaryText, setSummaryText] = useState<string>('');
  const [editedSummary, setEditedSummary] = useState<string>('');
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSummary = async () => {
      // Allow summary generation if any of template, custom_system_prompt, or custom_user_prompt exists
      if (question.id && (question.metadata?.template || question.metadata?.custom_system_prompt || question.metadata?.custom_user_prompt)) {  
        setIsLoadingSummary(true);
        setError(null);
        try {
          console.log('Loading summary with template:', question.metadata?.template || 'No template');
          console.log('Custom system prompt:', question.metadata?.custom_system_prompt || 'No custom system prompt');
          console.log('Custom user prompt:', question.metadata?.custom_user_prompt || 'No custom user prompt');
          
          const { summary } = await fetchQuestionSummary(
            question.id, 
            question.metadata?.template || '', // Pass empty string if no template
            question.metadata?.custom_user_prompt, // Pass custom user prompt if available
            question.metadata?.custom_system_prompt
          );
          setSummaryText(summary);
          setEditedSummary(summary);
        } catch (err) {
          setError('Failed to load summary.');
          console.error('Error loading summary:', err);
        } finally {
          setIsLoadingSummary(false);
        }
      } else {
        setError('No template, custom system prompt, or custom user prompt found for summary generation.');
      }
    };

    loadSummary();
  }, [question.id, question.metadata?.template]);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    try {
      await onSubmit({
        question: question.id as number,
        text_value: editedSummary,
      });
    } catch (err) {
      setError('Failed to submit response.');
      console.error('Error submitting response:', err);
    }
  };

  if (isLoadingSummary) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <TextField
        fullWidth
        multiline
        minRows={4}
        maxRows={12}
        value={editedSummary}
        onChange={(e) => setEditedSummary(e.target.value)}
        variant="outlined"
        sx={{ 
          mb: 3,
          '& .MuiInputBase-root': {
            minHeight: '150px',
            height: 'auto'
          },
          '& .MuiInputBase-input': {
            lineHeight: '1.5',
            fontSize: '1rem'
          }
        }}
      />
      <SubmitButton
        onSubmit={handleSubmit}
        isDisabled={editedSummary.trim() === ''}
        isLoading={isSubmitting}
        buttonText="Save & Continue"
      />
    </Box>
  );
};
